// src/lib/apiBase.ts
// Computes the API base URL with guards to avoid cross-origin calls from Vercel preview deployments.

const DEFAULT_BASE = import.meta.env.DEV ? "http://localhost:3000/api" : "/api";
const DEFAULT_SITE =
  import.meta.env.DEV
    ? "http://localhost:5173"
    : "https://www.bitewise.co.in";

function normalize(base: string) {
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

function normalizeSiteBase(base: string) {
  const normalized = normalize(base);
  try {
    const url = new URL(normalized);
    if (!import.meta.env.DEV && url.hostname === "bitewise.co.in") {
      url.hostname = "www.bitewise.co.in";
      url.protocol = "https:";
      return normalize(url.toString());
    }
  } catch {}
  return normalized;
}

export function resolveSiteBase(): string {
  const envBase = (import.meta.env.VITE_SITE_URL as string | undefined) || "";
  if (envBase) return normalizeSiteBase(envBase);
  if (typeof window !== "undefined" && window.location?.origin) {
    return normalizeSiteBase(window.location.origin);
  }
  return normalizeSiteBase(DEFAULT_SITE);
}

export function resolveApiBase(): string {
  const envBase = (import.meta.env.VITE_API_BASE as string | undefined) || "";
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const host = String(window.location.hostname || "").toLowerCase();
    const isLocalHost = host === "localhost" || host === "127.0.0.1";
    if (isLocalHost) {
      if (!envBase || envBase.startsWith("/")) {
        return normalize("http://localhost:3000/api");
      }
    }
  }
  // Guard against misconfigured provider base in prod (provider does not expose /dishes/*).
  if (envBase && /15\.235\.85\.18:7147/i.test(envBase)) {
    return normalize("/api");
  }
  const baseCandidate = envBase || DEFAULT_BASE;

  // In Vercel preview environments, prefer same-origin API to avoid CORS issues.
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    const isVercelPreview = /\.vercel\.app$/i.test(host);
    if (isVercelPreview) {
      if (envBase && /^https?:\/\//i.test(envBase)) {
        try {
          const parsed = new URL(envBase);
          if (parsed.hostname !== host) {
            return normalize("/api");
          }
        } catch {
          return normalize("/api");
        }
      } else if (envBase && envBase.startsWith("/")) {
        return normalize(envBase);
      } else {
        return normalize("/api");
      }
    }
  }

  return normalize(baseCandidate);
}

export function resolvePublicBase(): string {
  return `${resolveApiBase()}/public`;
}
